import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useConfirmedMutation } from '@/api/mutations';
import { fetchPostes, updatePosteFn, softDeletePosteFn, createPosteFn } from '@/api/endpoints';
import type { Poste, PosteCreateInput, PosteUpdateInput } from '@/api/types';

type SortKey = 'meteor' | 'load_type' | 'owner' | 'age';

const DATA_SOURCES: Record<number, string> = {
  0: 'NONE',
  1: 'METEOR_OI',
  2: 'METEO_FR',
  4: 'OVPF',
};

const LOAD_TYPE_OPTIONS: { v: number; l: string }[] = [
  { v: 1, l: 'WeeWX' },
  { v: 2, l: 'Json' },
  { v: 3, l: 'WeeWX_then_Json' },
  { v: 0, l: 'Aucun (0)' },
  { v: 16, l: 'MQTT (16)' },
];

function loadTypeLabel(v: number): string {
  switch (v) {
    case 1: return 'WeeWX';
    case 2: return 'Json';
    case 3: return 'WeeWX_then_Json';
    default: return 'Other';
  }
}

function fmtAge(sec: number | null): string {
  if (sec == null) return '—';
  const sign = sec < 0 ? '-' : '';
  const s = Math.abs(sec);
  if (s < 3600) return `${sign}${Math.round(s / 60)} min`;
  const h = s / 3600;
  if (h < 48) return `${sign}${Math.round(h)} h`;
  return `${sign}${Math.round(h / 24)} j`;
}

export default function Postes({ device }: { device: Device }) {
  const [sort, setSort] = useState<SortKey>('age');
  const [filterDS, setFilterDS] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<Poste | null>(null);
  const [creating, setCreating] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['postes', { data_source: filterDS, search: debouncedSearch }],
    queryFn: () => fetchPostes({ data_source: filterDS, search: debouncedSearch || undefined, limit: 500 }),
  });
  const postes = data?.postes ?? [];

  const createMut = useConfirmedMutation(
    (input: PosteCreateInput) => createPosteFn(input),
    {
      title: 'Créer un poste',
      message: 'Confirmer la création de ce poste ?',
      onSuccess: () => {
        toast.push('Poste créé', 'success');
        qc.invalidateQueries({ queryKey: ['postes'] });
        setCreating(false);
      },
    },
  );

  const updateMut = useConfirmedMutation(
    (vars: { id: number; patch: PosteUpdateInput }) => updatePosteFn(vars.id, vars.patch),
    {
      title: 'Modifier le poste',
      message: 'Confirmer la mise à jour de ce poste ?',
      onSuccess: () => {
        toast.push('Poste modifié', 'success');
        qc.invalidateQueries({ queryKey: ['postes'] });
        setSelected(null);
      },
    },
  );

  const deleteMut = useConfirmedMutation(
    (id: number) => softDeletePosteFn(id),
    {
      title: 'Désactiver le poste',
      message: 'Confirmer la désactivation (stop_date = now) ?',
      destructive: true,
      onSuccess: () => {
        toast.push('Poste désactivé', 'success');
        qc.invalidateQueries({ queryKey: ['postes'] });
        setSelected(null);
      },
    },
  );

  const filtered = useMemo(() => {
    return [...postes].sort((a, b) => {
      if (sort === 'age') {
        const va = a.last_obs_age_seconds, vb = b.last_obs_age_seconds;
        if (va == null) return 1;
        if (vb == null) return -1;
        return va - vb;
      }
      if (sort === 'load_type') return a.load_type - b.load_type;
      const va = a[sort as keyof Poste];
      const vb = b[sort as keyof Poste];
      return String(va ?? '').localeCompare(String(vb ?? ''));
    });
  }, [postes, sort]);

  return (
    <PageShell device={device} title="Postes" breadcrumb="Principal">
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="search"
            placeholder="Rechercher meteor, ville, type, owner…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm flex-1 min-w-[200px] max-w-md"
          />
          <select
            value={filterDS}
            onChange={(e) => setFilterDS(Number(e.target.value))}
            className="h-9 px-2.5 rounded-md border border-input bg-background text-sm"
          >
            {Object.entries(DATA_SOURCES).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">
            {isLoading ? 'Chargement…' : `${filtered.length} postes`}
          </span>
          <button
            onClick={() => setCreating(true)}
            className="ml-auto h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
            title="Ajouter un poste"
          >
            <span className="text-lg leading-none">+</span>
            <span className="hidden sm:inline">Nouveau poste</span>
          </button>
        </div>

        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <SortTh label="meteor"    active={sort === 'meteor'}    onClick={() => setSort('meteor')} />
                <SortTh label="load_type" active={sort === 'load_type'} onClick={() => setSort('load_type')} />
                <th className="px-4 py-2.5 text-left">api_key</th>
                <SortTh label="owner"     active={sort === 'owner'}     onClick={() => setSort('owner')} />
                <SortTh label="âge obs"   active={sort === 'age'}       onClick={() => setSort('age')} />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.slice(0, 200).map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-accent/30 cursor-pointer"
                  onClick={() => setSelected(p)}
                >
                  <td className="px-4 py-2.5 font-mono text-xs">{p.meteor}</td>
                  <td className="px-4 py-2.5">
                    <span className="badge-slate px-1.5 py-0.5 rounded text-[10px] font-medium">
                      {loadTypeLabel(p.load_type)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[10px] text-muted-foreground">
                    {p.api_key ? `${p.api_key.slice(0, 8)}…` : '—'}
                  </td>
                  <td className="px-4 py-2.5">{p.owner || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-right">{fmtAge(p.last_obs_age_seconds)}</td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm">
                    Aucun poste dans ce filtre.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <PosteDrawer
          poste={selected}
          mode="edit"
          onClose={() => setSelected(null)}
          onSave={(patch) => updateMut.mutate({ id: selected.id, patch: patch as PosteUpdateInput })}
          onDelete={() => deleteMut.mutate(selected.id)}
          isSaving={updateMut.isPending}
        />
      )}
      {creating && (
        <PosteDrawer
          mode="create"
          defaultDataSource={filterDS}
          onClose={() => setCreating(false)}
          onSave={(input) => createMut.mutate(input as PosteCreateInput)}
          isSaving={createMut.isPending}
        />
      )}
    </PageShell>
  );
}

function SortTh({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <th
      className={`px-4 py-2.5 text-left cursor-pointer ${active ? 'text-foreground' : ''}`}
      onClick={onClick}
    >
      {label}{active ? ' ↑' : ''}
    </th>
  );
}

type FormState = {
  meteor: string;
  delta_timezone: number;
  data_source: number;
  load_type: number;
  load_type_timeout: number;
  seuil_switch_mode: number;
  type: string;
  altitude: number;
  lat: number;
  long: number;
  other_code: string;
  owner: string;
  email: string;
  phone: string;
  quartier: string;
  city: string;
  country: string;
  comment: string;
  api_key: string;
};

function initForm(p?: Poste): FormState {
  return {
    meteor: p?.meteor ?? '',
    delta_timezone: p?.delta_timezone ?? 4,
    data_source: p?.data_source ?? 1,
    load_type: p?.load_type ?? 2,
    load_type_timeout: p?.load_type_timeout ?? 360,
    seuil_switch_mode: p?.seuil_switch_mode ?? 6,
    type: p?.type ?? '',
    altitude: p?.altitude ?? 0,
    lat: p?.lat ?? 0,
    long: p?.long ?? 0,
    other_code: p?.other_code ?? '',
    owner: p?.owner ?? '',
    email: p?.email ?? '',
    phone: p?.phone ?? '',
    quartier: p?.quartier ?? '',
    city: p?.city ?? '',
    country: p?.country ?? '',
    comment: p?.comment ?? '',
    api_key: p?.api_key ?? '',
  };
}

function PosteDrawer({
  poste,
  mode,
  defaultDataSource,
  onClose,
  onSave,
  onDelete,
  isSaving,
}: {
  poste?: Poste;
  mode: 'edit' | 'create';
  defaultDataSource?: number;
  onClose: () => void;
  onSave: (data: PosteCreateInput | PosteUpdateInput) => void;
  onDelete?: () => void;
  isSaving: boolean;
}) {
  const [f, setF] = useState<FormState>(() => {
    const base = initForm(poste);
    if (mode === 'create' && defaultDataSource != null) base.data_source = defaultDataSource;
    return base;
  });
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const isCreate = mode === 'create';

  return (
    <aside className="fixed inset-0 md:inset-auto md:top-0 md:right-0 md:bottom-0 md:w-[560px] bg-card border-l border-border shadow-2xl overflow-y-auto z-50 flex flex-col">
      <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">
            {isCreate ? 'Création' : <>Édition · <span className="font-mono">{poste?.meteor}</span></>}
          </div>
          <h2 className="text-lg font-bold">{isCreate ? 'Nouveau poste' : (poste?.city || '—')}</h2>
        </div>
        <button onClick={onClose} aria-label="Fermer" className="p-2 rounded-md hover:bg-muted">✕</button>
      </div>

      <div className="p-5 space-y-4 flex-1">
        {!isCreate && <ReadField label="id" value={String(poste!.id)} mono />}

        <Field label="meteor" hint={isCreate ? undefined : 'clé naturelle — non modifiable'}>
          <input
            value={f.meteor}
            onChange={(e) => set('meteor', e.target.value)}
            readOnly={!isCreate}
            className={`mt-1 w-full h-9 px-3 rounded-md border border-input text-sm font-mono ${isCreate ? 'bg-background' : 'bg-muted'}`}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="data_source">
            <select
              value={f.data_source}
              onChange={(e) => set('data_source', Number(e.target.value))}
              className="mt-1 w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm"
            >
              {Object.entries(DATA_SOURCES).map(([k, v]) => (
                <option key={k} value={k}>{v} ({k})</option>
              ))}
            </select>
          </Field>
          <Field label="load_type">
            <select
              value={f.load_type}
              onChange={(e) => set('load_type', Number(e.target.value))}
              className="mt-1 w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm"
            >
              {LOAD_TYPE_OPTIONS.some((o) => o.v === f.load_type)
                ? null
                : <option value={f.load_type}>Other ({f.load_type})</option>}
              {LOAD_TYPE_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <NumField label="delta_timezone" value={f.delta_timezone} onChange={(v) => set('delta_timezone', v)} />
          <TextField label="type" value={f.type} onChange={(v) => set('type', v)} mono />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="load_type_timeout (s)" value={f.load_type_timeout} onChange={(v) => set('load_type_timeout', v)} />
          <NumField label="seuil_switch_mode (min)" value={f.seuil_switch_mode} onChange={(v) => set('seuil_switch_mode', v)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <NumField label="altitude (m)" value={f.altitude} onChange={(v) => set('altitude', v)} />
          <NumField label="lat" value={f.lat} onChange={(v) => set('lat', v)} step="any" />
          <NumField label="long" value={f.long} onChange={(v) => set('long', v)} step="any" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <TextField label="owner" value={f.owner} onChange={(v) => set('owner', v)} />
          <TextField label="other_code" value={f.other_code} onChange={(v) => set('other_code', v)} mono />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="email" value={f.email} onChange={(v) => set('email', v)} />
          <TextField label="phone" value={f.phone} onChange={(v) => set('phone', v)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <TextField label="quartier" value={f.quartier} onChange={(v) => set('quartier', v)} />
          <TextField label="city" value={f.city} onChange={(v) => set('city', v)} />
          <TextField label="country" value={f.country} onChange={(v) => set('country', v)} />
        </div>

        <Field label="comment">
          <textarea
            value={f.comment}
            onChange={(e) => set('comment', e.target.value)}
            rows={3}
            className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
          />
        </Field>

        {!isCreate && (
          <Field label="api_key" hint="vide = aucune clé">
            <input
              value={f.api_key}
              onChange={(e) => set('api_key', e.target.value)}
              className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-mono"
            />
          </Field>
        )}

        {!isCreate && (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
            <div className="col-span-2">
              <ReadField
                label="Dernière mesure traitée"
                value={`${poste!.last_obs_date_local ?? '—'} au ${poste!.last_obs_end_date_local ?? '—'}`}
                mono
              />
            </div>
            <ReadField label="last_obs_id" value={poste!.last_obs_id ?? '—'} mono />
            <ReadField label="âge obs" value={fmtAge(poste!.last_obs_age_seconds)} mono />
            <div className="col-span-2">
              <ReadField label="info (jsonb)" value={poste!.info ? JSON.stringify(poste!.info) : '—'} mono />
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border px-5 py-3 flex gap-2 justify-between">
        {!isCreate && onDelete ? (
          <button
            onClick={onDelete}
            className="h-9 px-4 rounded-md border border-destructive text-destructive text-sm hover:bg-destructive/10"
          >
            Désactiver
          </button>
        ) : <span />}
        <div className="flex gap-2">
          <button onClick={onClose} className="h-9 px-4 rounded-md border border-border text-sm">Annuler</button>
          <button
            onClick={() => onSave(buildPayload(f, mode))}
            disabled={isSaving || (isCreate && !f.meteor.trim())}
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
          >
            {isSaving ? 'Sauvegarde…' : isCreate ? 'Créer' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </aside>
  );
}

function buildPayload(f: FormState, mode: 'edit' | 'create'): PosteCreateInput | PosteUpdateInput {
  const common = {
    delta_timezone: f.delta_timezone,
    data_source: f.data_source,
    load_type: f.load_type,
    load_type_timeout: f.load_type_timeout,
    seuil_switch_mode: f.seuil_switch_mode,
    type: f.type,
    altitude: f.altitude,
    lat: f.lat,
    long: f.long,
    other_code: f.other_code,
    owner: f.owner,
    email: f.email,
    phone: f.phone,
    quartier: f.quartier,
    city: f.city,
    country: f.country,
    comment: f.comment,
  };
  // create includes meteor; edit omits it (natural key) but allows api_key
  return mode === 'create'
    ? { meteor: f.meteor, ...common }
    : { ...common, api_key: f.api_key.trim() === '' ? null : f.api_key };
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="font-medium">{label}</span>
      {hint && <span className="ml-2 text-[10px] text-muted-foreground">{hint}</span>}
      {children}
    </label>
  );
}

function ReadField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="text-sm">
      <span className="font-medium">{label}</span>
      <div className={`mt-1 w-full min-h-9 px-3 py-1.5 rounded-md border border-input bg-muted text-xs break-all ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, mono }: { label: string; value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <Field label={label}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm ${mono ? 'font-mono' : ''}`}
      />
    </Field>
  );
}

function NumField({ label, value, onChange, step }: { label: string; value: number; onChange: (v: number) => void; step?: string }) {
  return (
    <Field label={label}>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-mono"
      />
    </Field>
  );
}
